// Session 52 · Interactive Leaflet map for /spaces.
//
// Uses vanilla Leaflet via useEffect instead of react-leaflet's MapContainer,
// which trips React 18 StrictMode / ErrorBoundary reattach cycles with
// "Map container is already initialized." on the same DOM node. Vanilla L.map
// + a manual cleanup makes remount idempotent.
//
// Session 53 · Marker clustering via leaflet.markercluster. Featured venues
// render with a gold pin; standard venues use the default blue pin. Cluster
// bubbles are gold-tinted when they contain a featured venue, gold-outline
// otherwise. Click a cluster to zoom + expand.
//
// - Renders one pin per workspace that has lat/lng (falls back to venue coords).
// - Auto-fits bounds to the current filter set. When radius search is active,
//   also renders the search radius circle centered at (lat, lng).
// - Popup shows name + city + a "View →" link.
// - Uses OpenStreetMap tiles (free, no key).
// - Collapsible: users toggle the map on/off. Persists in localStorage.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { overrideDefaultIcon } from "../lib/leafletIcons";

overrideDefaultIcon();

// Session 53 · Gold pin for featured venues so they stand out on the map
// (mirrors the FeaturedBadge treatment on the grid cards).
const FEATURED_ICON = new L.Icon({
  iconUrl: "data:image/svg+xml;utf8," + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="42" viewBox="0 0 26 42">
      <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 29 13 29s13-19.25 13-29c0-7.18-5.82-13-13-13z" fill="#B8913A" stroke="#5b4218" stroke-width="1.2"/>
      <circle cx="13" cy="13" r="5.5" fill="#f7e1a8"/>
    </svg>`),
  iconSize: [26, 42], iconAnchor: [13, 42], popupAnchor: [1, -38],
});

function popupHtml(s, isFeatured) {
  const line2 = [s.neighborhood, s.city].filter(Boolean).join(" · ") || s.state || "";
  const type = s.type ? `<div style="font-size:11px;color:#888;margin-bottom:6px;">${s.type}</div>` : "";
  const featuredBadge = isFeatured
    ? `<span style="display:inline-block;background:#B8913A;color:#fff;font-size:9px;letter-spacing:0.14em;text-transform:uppercase;padding:2px 6px;border-radius:3px;margin-bottom:6px;">Featured</span>`
    : "";
  const slug = encodeURIComponent(s.slug || "");
  return `
    <div style="min-width:170px;">
      ${featuredBadge}
      <div style="font-weight:600;margin-bottom:4px;">${s.name || ""}</div>
      <div style="font-size:12px;color:#555;margin-bottom:6px;">${line2}</div>
      ${type}
      <a href="/spaces/${slug}" style="font-size:12px;color:#B8913A;font-weight:600;text-decoration:none;">View details →</a>
    </div>`;
}

// Session 53 · Custom cluster icon — gold when the cluster contains any
// featured venue, silver-navy otherwise. Uses `getAllChildMarkers` to peek
// at the featured flag on each contained marker's `options`.
function clusterIcon(cluster) {
  const markers = cluster.getAllChildMarkers();
  const hasFeatured = markers.some(m => m.options.isFeatured);
  const count = cluster.getChildCount();
  const bg = hasFeatured ? "#B8913A" : "rgba(26,35,64,0.92)";
  const border = hasFeatured ? "#5b4218" : "#B8913A";
  const color = hasFeatured ? "#fff" : "#B8913A";
  return L.divIcon({
    html: `
      <div style="
        background:${bg};color:${color};
        width:38px;height:38px;border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-weight:700;font-size:14px;font-family:system-ui;
        border:2px solid ${border};
        box-shadow:0 4px 12px rgba(0,0,0,0.35);
      "><span>${count}</span></div>`,
    className: "metier-cluster",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

export default function SpacesMap({ spaces, radiusMiles, geo, featuredKeys }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("spaces_map_open") !== "0"; }
    catch { return true; }
  });
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({ markers: null, circle: null });
  // Session 59.5 · P0 · Live ref to the current fallback center. Refs are
  // NOT reactive deps, so the init effect can read the latest value without
  // needing to declare it as a dependency (which would rebuild the map on
  // every geo change). Kept in sync by the effect below.
  const fallbackCenterRef = useRef([39.5, -98]);

  useEffect(() => {
    try { localStorage.setItem("spaces_map_open", open ? "1" : "0"); }
    catch { /* localStorage disabled */ }
  }, [open]);

  const withCoords = useMemo(
    () => (spaces || []).filter(s =>
      typeof s.latitude === "number" && typeof s.longitude === "number"
      && !Number.isNaN(s.latitude) && !Number.isNaN(s.longitude)
    ),
    [spaces],
  );

  // Session 59.5 · P0 · Memoize `fallbackCenter` so the "initialize map"
  // effect can declare it as a dependency without re-initializing the map
  // on every render (which would flicker + re-fetch tiles). The identity
  // only changes when the caller's geo actually moves.
  const fallbackCenter = useMemo(
    () => (geo?.lat != null && geo?.lng != null
      ? [geo.lat, geo.lng]
      : [39.5, -98]),
    [geo?.lat, geo?.lng],
  );

  const radiusMeters = radiusMiles && geo ? Number(radiusMiles) * 1609.34 : null;

  // Session 59.5 · P0 · useCallback so the effect below can list it as a
  // dep without recreating on every render. featuredKeys is the only
  // dynamic input; when it changes we DO want the marker layer to refresh.
  const isFeatured = useCallback((s) => {
    if (!featuredKeys || featuredKeys.size === 0) return false;
    if (s.slug && featuredKeys.has(`slug:${s.slug}`)) return true;
    if (s.workspace_id && featuredKeys.has(`workspace:${s.workspace_id}`)) return true;
    return false;
  }, [featuredKeys]);

  // Session 59.5 · P0 · Keep the ref in sync with the memoized value.
  // The init effect reads this ref for its initial `center:` — refs are
  // not reactive so this pattern is lint-clean without any suppression.
  useEffect(() => { fallbackCenterRef.current = fallbackCenter; }, [fallbackCenter]);

  // Initialize / tear down the Leaflet map. `open` toggles this whole effect
  // so hidden state doesn't hold a live map instance.
  useEffect(() => {
    if (!open || !containerRef.current) return undefined;
    const el = containerRef.current;
    // Guard against React StrictMode double-invocation.
    if (el._leaflet_id) {
      try { delete el._leaflet_id; } catch { /* noop */ }
    }
    const map = L.map(el, {
      center: fallbackCenterRef.current,
      zoom: 4,
      scrollWheelZoom: false,
      zoomControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    // Session 53 · MarkerClusterGroup replaces the plain layerGroup so dense
    // regions collapse into a numbered bubble. Smooth spiderfy animation is
    // enabled by default; chunkedLoading keeps the UI responsive with 200+ pins.
    layersRef.current.markers = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      maxClusterRadius: 55,
      iconCreateFunction: clusterIcon,
      animate: true,
      animateAddingMarkers: true,
    });
    map.addLayer(layersRef.current.markers);
    return () => {
      try {
        map.remove();
      } catch { /* noop */ }
      mapRef.current = null;
      layersRef.current = { markers: null, circle: null };
    };
  }, [open]);

  // Push markers + radius circle + fit bounds whenever the filter set changes.
  useEffect(() => {
    const map = mapRef.current;
    const markerGroup = layersRef.current.markers;
    if (!map || !markerGroup) return;
    markerGroup.clearLayers();
    if (layersRef.current.circle) {
      layersRef.current.circle.remove();
      layersRef.current.circle = null;
    }
    // Markers — featured venues get gold pins + carry an isFeatured flag so
    // the cluster icon-creator can detect featured contents.
    withCoords.forEach(s => {
      const featured = isFeatured(s);
      const marker = L.marker(
        [s.latitude, s.longitude],
        { icon: featured ? FEATURED_ICON : new L.Icon.Default(),
          isFeatured: featured },
      );
      marker.bindPopup(popupHtml(s, featured));
      markerGroup.addLayer(marker);
    });
    // Radius circle
    if (radiusMeters && geo) {
      layersRef.current.circle = L.circle([geo.lat, geo.lng], {
        radius: radiusMeters,
        color: "#B8913A",
        fillColor: "#B8913A",
        fillOpacity: 0.08,
        weight: 1.5,
      }).addTo(map);
    }
    // Fit bounds
    const points = withCoords.map(s => [s.latitude, s.longitude]);
    if (radiusMeters && geo) points.push([geo.lat, geo.lng]);
    if (points.length === 0) {
      map.setView(fallbackCenter, 4);
    } else if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 8 });
    }
    // Fix rendering when the container's size changed between renders.
    setTimeout(() => { try { map.invalidateSize(); } catch { /* noop */ } }, 0);
  }, [withCoords, radiusMeters, geo?.lat, geo?.lng, geo, isFeatured, fallbackCenter, featuredKeys]);

  return (
    <div data-testid="spaces-map-wrap" style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>
          Map view · {withCoords.length} of {spaces?.length || 0} pinned
        </div>
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          data-testid="spaces-map-toggle"
          style={{
            background: "none", border: "1px solid rgba(212,168,75,0.32)",
            color: "var(--gold, #B8913A)", padding: "6px 14px", borderRadius: 999,
            fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          {open ? "Hide map" : "Show map"}
        </button>
      </div>
      {open && (
        <div
          ref={containerRef}
          data-testid="spaces-map"
          style={{
            height: 380, borderRadius: 14, overflow: "hidden",
            border: "1px solid rgba(212,168,75,0.22)",
            boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
            background: "rgba(255,255,255,0.02)",
          }}
        />
      )}
      {open && withCoords.length === 0 && spaces?.length > 0 && (
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 6 }}>
          None of the current listings have coordinates yet. Filter by state / ZIP or ask an admin to geocode.
        </div>
      )}
    </div>
  );
}
